import type {
	CreatedField,
	FieldOperations,
	FieldOptions as FieldOptionsOnServer,
	FieldProvider,
	UpdatedField,
} from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type {
	FieldUuid,
	GroupUuid,
	IssueTypeUuid,
	ProjectUuid,
} from '@atlassian/jira-shared-types/src/general.tsx';
import type { IssueLayoutOwner } from './issue/issue-layout-owner.tsx';
import type { IssueTypeProperties } from './issue/issue-type-properties.tsx';
import type { IssueTypeOperations } from './issue/issue-types.tsx';
import type { ActiveLayoutItem, LayoutItemOperations, SwiftTabWithLinks } from './layout-items.tsx';

export type OwnerType = 'ISSUE_TYPE' | 'REQUEST_TYPE';
export type IssueLayoutType = 'REQUEST_FORM' | 'ISSUE_VIEW';

export const FIELD = 'FIELD' as const;
export const GROUP = 'GROUP' as const;
export const PANEL = 'PANEL' as const;
export const TAB = 'TAB' as const;

export type FieldResponseData = {
	// For system fields, this contains its name ('reporter', 'assignee', ...).
	// For custom fields, this is 'customfield_<randomnumber>'.
	// Unique in Project context
	// No PII
	key: string;
	externalUuid: FieldUuid;
	// Human readable name. May contain PII
	name: string;
	// Human readable description. May contain PII
	description: string;
	// type may be 'text' | 'checkbox' | 'date' ... data type of the field
	// no PII
	type: string;
	// true = custom, false = system field
	custom: boolean;
	// We only check this when custom = true
	// true = global custom field -> we treat the field as a system field
	global: boolean;
	// true = this field must be filled in all issues, false = may stay empty
	required: boolean;
	operations: FieldOperations;
	provider?: FieldProvider;
	// There are different structures for different fields but all of them are arrays
	options?: FieldOptionsOnServer;
	// There are different structures for different fields
	defaultValue?: unknown;
	// anyone can write anything into properties
	properties?: null | Record<string, unknown>;
	configuration?: {
		[key: string]: unknown;
	};
};

export type FieldResponse = {
	type: typeof FIELD;
	// Section type is just a string, because the list of
	// available sections can vary between issue layouts.
	// Corresponds to the old field `category`
	sectionType: string;
	key: string;
	data: FieldResponseData;
};

export type FieldRequestData = FieldResponseData & {
	// these aren't always present in swift but we expect them to be returned by the backend
	key?: string;
	global?: boolean;
};

export type FieldRequest = FieldResponse & {
	key?: string | null;
	data: FieldRequestData;
};

export type PanelOnServer = {
	type: typeof PANEL;
	sectionType: string;
	// Unique in Project context
	// No PII
	key: string;
	data: {
		name: string;
		operations: LayoutItemOperations;
	};
};

type GroupItem = FieldResponse | PanelOnServer;

// A group of other items.
export type Group = {
	type: typeof GROUP;
	sectionType: string;
	data: {
		id: GroupUuid;
		// Unique in Project context
		// No PII
		key: string;
		items: GroupItem[];
	};
};

// A legacy group of other items with different presentation.
export type Tab = {
	type: typeof TAB;
	sectionType: string;
	// Unique in Project context
	// No PII
	key: string;
	data: {
		name: string;
	};
	items: GroupItem[];
};

export type ActiveLayoutItemOnServer = FieldResponse | Group | PanelOnServer | Tab;

// request has some properties marked as optional that we expect to be filled in the response
export type IssueLayoutRequest = {
	items: ReadonlyArray<FieldRequest | Group | PanelOnServer | Tab>;
};

export type IssueLayoutResponse = {
	items: ReadonlyArray<ActiveLayoutItemOnServer>;
};

export type UpdateIssueLayoutRequest = {
	owners: IssueLayoutOwner[];
	issueLayout: IssueLayoutRequest;
};

export type UpdateIssueLayoutApiRequest = {
	projectId: number;
	extraDefinerId: number;
	issueLayoutType: IssueLayoutType;
	owners: IssueLayoutOwner[];
	issueLayoutConfig: IssueLayoutRequest;
};

export type UpdateIssueLayoutApiResponse = {
	owners: IssueLayoutOwner[];
	issueLayoutConfig: IssueLayoutResponse;
	issueLayoutType: IssueLayoutType;
	extraDefinerId: number;
	name?: string;
	description?: string;
};

export type UpdateIssueTypeResponseForSwift = {
	properties?: IssueTypeProperties;
	name: string;
	description: string;
	avatarId: number;
	activeLayoutItems: ActiveLayoutItem[];
};

export type UpdateIssueLayoutRequestFromSwift = {
	projectUuid: ProjectUuid | undefined;
	externalUuid: IssueTypeUuid | undefined;
	name: string;
	description: string;
	avatarId: number;
	fields: (UpdatedField | CreatedField)[];
	properties?: IssueTypeProperties;
	operations?: IssueTypeOperations;
	/**
	 * Used to update the layout items in the new issue layout API.
	 */
	readonly layoutItems: ActiveLayoutItem[];
	readonly issueLayoutOwners: IssueLayoutOwner[];
	readonly tabs: {
		[fieldCategoryName: string]: SwiftTabWithLinks[];
	};
};

export type SaveChangesServiceParams = {
	projectContext?: ProjectContext;
	projectId: number;
	layoutId: string;
	extraDefinerId: number;
	data: UpdateIssueLayoutRequestFromSwift;
};

export type SaveChangesServiceResponse = {
	owners: IssueLayoutOwner[];
	activeLayoutItems: ReadonlyArray<ActiveLayoutItem>;
	issueLayoutType: IssueLayoutType;
	extraDefinerId: number;
	name?: string;
	description?: string;
};

export type SaveChangesService = (
	updateRequest: SaveChangesServiceParams,
) => Promise<SaveChangesServiceResponse>;

export type TransformResponse = (
	request: UpdateIssueLayoutRequestFromSwift,
	response: UpdateIssueLayoutApiResponse,
) => UpdateIssueTypeResponseForSwift;
